import { useState, useEffect, useCallback } from 'react';
import { dashboardService } from '../services/analytics';
import { Alert, AlertsResponse } from '../types/analytics';
import { getApiErrorMessage } from '../utils/errors';

export const useAlerts = (providerId: number | null, days: number = 7, severity?: string) => {
  const [alerts, setAlerts] = useState<Alert[]>([]);
  const [total, setTotal] = useState(0);
  const [unacknowledgedCount, setUnacknowledgedCount] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const fetchAlerts = useCallback(async () => {
    if (!providerId) {
      setLoading(false);
      return;
    }

    try {
      setLoading(true);
      setError(null);
      const data: AlertsResponse = await dashboardService.getAlerts(providerId, days, severity);
      setAlerts(data.alerts);
      setTotal(data.total_alerts);
      setUnacknowledgedCount(data.unacknowledged_count);
    } catch (err: any) {
      setError(getApiErrorMessage(err, 'Failed to fetch alerts'));
      console.error('Error fetching alerts:', err);
    } finally {
      setLoading(false);
    }
  }, [providerId, days, severity]);

  const acknowledgeAlert = useCallback(async (alertId: string) => {
    try {
      await dashboardService.acknowledgeAlert(alertId);
      // Update local state
      setAlerts((prev) =>
        prev.map((alert) => (alert.alert_id === alertId ? { ...alert, acknowledged: true } : alert))
      );
      setUnacknowledgedCount((prev) => Math.max(0, prev - 1));
    } catch (err: any) {
      setError(getApiErrorMessage(err, 'Failed to acknowledge alert'));
      console.error('Error acknowledging alert:', err);
    }
  }, []);

  useEffect(() => {
    fetchAlerts();

    // Auto-refresh every 2 minutes
    const interval = setInterval(fetchAlerts, 2 * 60 * 1000);

    return () => clearInterval(interval);
  }, [fetchAlerts]);

  return {
    alerts,
    total,
    unacknowledgedCount,
    loading,
    error,
    refresh: fetchAlerts,
    acknowledgeAlert,
  };
};
